import { useTranslation } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { getCurrentOrgId, getPrincipalObjectExportLimit, isPrincipalImportExportEnabled, isScriptabilityEnabled, isTmlObjectIdEnabled, } from '@thoughtspot/session-service';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { useAddUsersToOrgMutation, useAssignUsersToGroupsFromUserMutation, useDeleteUserMutation, useGenerateOtpMutation, useReInviteMemberMutation, useUpdateUserMutation, } from "../../../../services/user-group/user-group-service";
import { BasePageContext } from "../../../../contexts/basepage-context";
import { isFreeTrialOrTeamOrDeveloperUser } from '../../../user-management/users/util';
import { captureFailureEvent, isExportObjectLimitReached, MODE, } from '../../../user-management/util';
import styles from './components/users.module.scss';
import { UsersList } from './components/UsersList';
import { UsersModals } from './components/UsersModals';
import { ActiveModal, ListActionTypeV2, TableColumnId, UserStateTransition, V2TranslationKeys, } from './constants';
import { useExportUsers } from './hooks/useExportUsers';
import { useOrgNameMap } from './hooks/useOrgNameMap';
import { useSplitButtonDropdown } from './hooks/useSplitButtonDropdown';
import { useUsersList } from './hooks/useUsersList';
import { cleanUserPayload, getBulkActions, getColumnDefinitionsV2, handleGraphQLError, } from './utils';
const UsersContainer = ({ currentOrgId: propOrgId, }) => {
    const { t } = useTranslation();
    const { metadataList } = BasePageContext();
    const currentOrgId = propOrgId ?? getCurrentOrgId();
    const isClusterAdmin = currentOrgId === -1;
    const isTmlObjectIdFlagEnabled = isTmlObjectIdEnabled();
    const isTmlEnabled = isPrincipalImportExportEnabled() && isScriptabilityEnabled();
    const [currentUser, setCurrentUser] = useState(null);
    const [activeModal, setActiveModal] = useState(ActiveModal.NONE);
    const [settingsInitialTab, setSettingsInitialTab] = useState('scim');
    const [toastMessage, setToastMessage] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const showError = useCallback((msg) => setErrorMessage(msg), []);
    const hideError = useCallback(() => setErrorMessage(null), []);
    const closeModal = useCallback(() => setActiveModal(ActiveModal.NONE), []);
    useEffect(() => {
        if (sessionStorage.getItem('MfaAcceptedOpenSettings') === 'true') {
            sessionStorage.removeItem('MfaAcceptedOpenSettings');
            setSettingsInitialTab('mfaSettings');
            setActiveModal(ActiveModal.SETTINGS);
        }
    }, []);
    const { orgNameMap, orgsListLoading } = useOrgNameMap({
        skip: !isClusterAdmin,
    });
    const { rowData, gridLoading, isLoading, setIsLoading, selectedRowsIds, setSelectedRowsIds, userListInput, isLastBatch, refreshUsersList, onChangeOffset, onSearchChange, onSortingChanged, onRowsSelected, onGridReady, } = useUsersList({ isClusterAdmin, orgNameMap, orgsListLoading });
    const { isOpen: isDropdownOpen, setIsOpen: setIsDropdownOpen, chevronRef: chevronButtonRef, dropdownRef, } = useSplitButtonDropdown();
    const { shouldExportDependencies, isGetEdocLoading, exportUsers, onExportRadioChange, } = useExportUsers({
        isClusterAdmin,
        isTmlObjectIdFlagEnabled,
        onError: showError,
        onClose: closeModal,
        clearSelection: () => setSelectedRowsIds([]),
        setIsLoading,
    });
    const [generatedOtp, setGeneratedOtp] = useState('');
    const [otpErrorMessage, setOtpErrorMessage] = useState('');
    const [showOtpLoader, setShowOtpLoader] = useState(false);
    const { deleteUser, loading: deleteUserLoading } = useDeleteUserMutation();
    const { updateUser } = useUpdateUserMutation();
    const { generateOtp } = useGenerateOtpMutation();
    const { assignUsersToGroupsFromUser, } = useAssignUsersToGroupsFromUserMutation();
    const { addUsersToOrg } = useAddUsersToOrgMutation();
    const { data: reInviteData, error: reInviteError, loading: reInviteLoading, reInviteMember, } = useReInviteMemberMutation();
    const onSuccess = (successMode) => {
        hideError();
        refreshUsersList({ data: { ...userListInput } });
        const messageMap = {
            [MODE.DELETE]: t(V2TranslationKeys.DELETE_SUCCESS),
            [MODE.ADD]: t(V2TranslationKeys.ADD_SUCCESS),
            [MODE.UPDATE]: t(V2TranslationKeys.UPDATE_SUCCESS),
            [MODE.ASSIGN_USERS_GROUPS]: t(V2TranslationKeys.UPDATE_SUCCESS),
            [MODE.ASSIGN_USERS_ORG]: t(V2TranslationKeys.UPDATE_SUCCESS),
            [MODE.RESEND_ACTIVATION]: t(V2TranslationKeys.ACTIVATION_MAIL_SENT),
        };
        setToastMessage(messageMap[successMode] || t(V2TranslationKeys.UPDATE_SUCCESS));
    };
    useEffect(() => {
        if (!reInviteLoading) {
            if (!_.isNil(reInviteData)) {
                onSuccess(MODE.RESEND_ACTIVATION);
                setIsLoading(false);
            }
            if (reInviteError) {
                showError(t(V2TranslationKeys.ACTIVATION_MAIL_ERROR));
                setIsLoading(false);
            }
        }
    }, [reInviteData, reInviteError, reInviteLoading]);
    const updateUserState = async (userId, state) => {
        setIsLoading(true);
        try {
            await updateUser({
                userid: userId,
                content: {},
                password: '',
                userAccountState: state,
                isAllOrgContext: isClusterAdmin,
                triggeredbyadmin: true,
            });
            if (state === UserStateTransition.REACTIVATE) {
                onSuccess(MODE.RESEND_ACTIVATION);
            }
            else {
                refreshUsersList({ data: { ...userListInput } });
            }
        }
        catch {
            if (state === UserStateTransition.REACTIVATE) {
                showError(t(V2TranslationKeys.ACTIVATION_MAIL_ERROR));
            }
        }
        finally {
            setIsLoading(false);
        }
    };
    const onDeleteConfirm = async () => {
        hideError();
        const ids = [...selectedRowsIds];
        if (!ids.length)
            return;
        try {
            await deleteUser(ids, isClusterAdmin);
            closeModal();
            onSuccess(MODE.DELETE);
            setSelectedRowsIds([]);
            const { offset, batchSize } = userListInput;
            if (offset && ids.length === rowData.length) {
                onChangeOffset(offset - batchSize);
            }
        }
        catch (err) {
            const { graphQlError, info } = handleGraphQLError(err, showError, t);
            setSelectedRowsIds([]);
            closeModal();
            const failureAction = new UserAction(userActionTypeEnum.DELETE_USERS);
            captureFailureEvent(failureAction.name, graphQlError, t(info.summary));
        }
    };
    const onAddToGroupsConfirm = async (payload) => {
        hideError();
        const cleaned = payload?.map((user) => cleanUserPayload(user));
        try {
            await assignUsersToGroupsFromUser({ content: cleaned });
            onSuccess(MODE.ASSIGN_USERS_GROUPS);
            setSelectedRowsIds([]);
            closeModal();
        }
        catch (err) {
            handleGraphQLError(err, showError, t);
            setSelectedRowsIds([]);
            closeModal();
        }
    };
    const onAddToOrgConfirm = async (orgId) => {
        hideError();
        try {
            await addUsersToOrg({
                userids: selectedRowsIds,
                orgid: Number(orgId),
            });
            onSuccess(MODE.ASSIGN_USERS_ORG);
            setSelectedRowsIds([]);
            closeModal();
        }
        catch (err) {
            handleGraphQLError(err, showError, t);
            setSelectedRowsIds([]);
            closeModal();
        }
    };
    const bulkUpdateUserState = async (state) => {
        setIsLoading(true);
        try {
            await Promise.all(selectedRowsIds.map(id => updateUser({
                userid: id,
                content: {},
                password: '',
                userAccountState: state,
                isAllOrgContext: isClusterAdmin,
                triggeredbyadmin: true,
            })));
            onSuccess(MODE.UPDATE);
        }
        catch (err) {
            handleGraphQLError(err, showError, t);
        }
        finally {
            setIsLoading(false);
        }
    };
    const onRowActionRef = useRef(() => { });
    onRowActionRef.current = ({ action, data }) => {
        setCurrentUser(data);
        switch (action) {
            case ListActionTypeV2.VIEW_DETAILS:
                setActiveModal(ActiveModal.USER_DETAILS);
                break;
            case ListActionTypeV2.EDIT:
                setActiveModal(ActiveModal.EDIT_USER);
                break;
            case ListActionTypeV2.DELETE:
                setActiveModal(ActiveModal.DELETE);
                break;
            case ListActionTypeV2.EXPORT:
                exportUsers([data.id]);
                break;
            case ListActionTypeV2.RESEND_ACTIVATION_MAIL:
                if (isFreeTrialOrTeamOrDeveloperUser()) {
                    setIsLoading(true);
                    reInviteMember(data.id);
                }
                else {
                    updateUserState(data.id, UserStateTransition.REACTIVATE);
                }
                break;
            case ListActionTypeV2.UNLOCK:
                updateUserState(data.id, UserStateTransition.UNLOCK);
                break;
            case ListActionTypeV2.UNSUSPEND:
                updateUserState(data.id, UserStateTransition.UNSUSPEND);
                break;
            case ListActionTypeV2.DEACTIVATE:
                updateUserState(data.id, UserStateTransition.DEACTIVATE);
                break;
            case ListActionTypeV2.ACTIVATE:
                updateUserState(data.id, UserStateTransition.ACTIVATE);
                break;
            case ListActionTypeV2.RESET_PASSWORD:
                setGeneratedOtp('');
                setOtpErrorMessage('');
                setShowOtpLoader(false);
                setActiveModal(ActiveModal.RESET_PASSWORD);
                break;
            default:
                break;
        }
    };
    const onGenerateOtp = async () => {
        if (!currentUser)
            return;
        setShowOtpLoader(true);
        setOtpErrorMessage('');
        try {
            const response = await generateOtp(currentUser.id);
            if (response?.data?.Session__generateOtp) {
                setGeneratedOtp(response.data.Session__generateOtp);
            }
            else {
                setOtpErrorMessage(V2TranslationKeys.OTP_GENERATION_FAILED);
            }
        }
        catch {
            setOtpErrorMessage(V2TranslationKeys.OTP_GENERATION_FAILED);
        }
        finally {
            setShowOtpLoader(false);
        }
    };
    const onRowActionCB = useCallback((payload) => onRowActionRef.current(payload), []);
    const bulkHandlers = {
        onDelete: () => setActiveModal(ActiveModal.DELETE),
        onExport: () => {
            if (isExportObjectLimitReached(selectedRowsIds)) {
                showError(t(V2TranslationKeys.EXPORT_LIMIT_ERROR, {
                    limit: getPrincipalObjectExportLimit(),
                }));
            }
            else {
                setActiveModal(ActiveModal.EXPORT);
            }
        },
        onAddToGroups: () => setActiveModal(ActiveModal.ADD_TO_GROUPS),
        onAddToOrg: () => setActiveModal(ActiveModal.ADD_TO_ORG),
        onUnlockAll: () => bulkUpdateUserState(UserStateTransition.UNLOCK),
        onUnsuspendAll: () => bulkUpdateUserState(UserStateTransition.UNSUSPEND),
        onActivateAll: () => bulkUpdateUserState(UserStateTransition.ACTIVATE),
    };
    const selectedUsers = useMemo(() => rowData.filter(u => selectedRowsIds.includes(u.id)), [rowData, selectedRowsIds]);
    const bulkActions = selectedRowsIds.length > 0
        ? getBulkActions(selectedUsers, bulkHandlers, isClusterAdmin)
        : [];
    const listViewProps = {
        data: rowData,
        columns: getColumnDefinitionsV2(isClusterAdmin),
        options: {
            suppressMovable: true,
            hasCheckboxes: true,
            onRowsSelected,
            selectedRowsIds,
            checkboxHeaderClass: [styles.headerStyle],
            customWidth: 48,
        },
        agGridContext: {
            onRowAction: onRowActionCB,
            orgNameMap,
            isClusterAdmin,
        },
        onSortingChanged,
        loading: gridLoading,
        setAgGridApi: onGridReady,
        suppressKeyboardEvent: (params) => params.colDef.colId === TableColumnId.ACTIONS,
    };
    const onCreateUserSuccess = () => {
        hideError();
        refreshUsersList({ data: { ...userListInput } });
        setToastMessage(t('users.wizard.toast.addSuccess'));
        closeModal();
    };
    const onEditUserSuccess = () => {
        hideError();
        refreshUsersList({ data: { ...userListInput } });
        setToastMessage(t(V2TranslationKeys.EDIT_SUCCESS));
        closeModal();
    };
    const onImportUsers = () => {
        localStorage.setItem('allOrgsOverride', isClusterAdmin ? 'true' : 'false');
        window.location.href = `${window.location.origin}/#/import-tsl/USER`;
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(UsersList, { rowData: rowData, selectedRowsIds: selectedRowsIds, isLoading: isLoading, bulkActions: bulkActions, metadataList: metadataList, listViewProps: listViewProps, pagination: {
                offset: userListInput.offset,
                batchSize: userListInput.batchSize,
                isLastBatch,
                onChangeOffset,
            }, splitButton: {
                isTmlEnabled,
                isDropdownOpen,
                chevronButtonRef,
                dropdownRef,
                setIsDropdownOpen,
            }, feedback: {
                errorMessage,
                toastMessage,
                hideError,
                setToastMessage,
            }, onSearchChange: onSearchChange, setActiveModal: setActiveModal, onImportUsers: onImportUsers }),
        React.createElement(UsersModals, { activeModal: activeModal, currentUser: currentUser, selectedRowsIds: selectedRowsIds, isClusterAdmin: isClusterAdmin, currentOrgId: currentOrgId, deleteUserLoading: deleteUserLoading, closeModal: closeModal, setActiveModal: setActiveModal, orgNameMap: orgNameMap, settingsInitialTab: settingsInitialTab, exportProps: {
                isGetEdocLoading,
                shouldExportDependencies,
                onRadioChange: onExportRadioChange,
                exportUsers,
            }, callbacks: {
                onDeleteConfirm,
                onAddToGroupsConfirm,
                onAddToOrgConfirm,
                onResetPasswordConfirm: closeModal,
                onGenerateOtp,
                generatedOtp,
                otpErrorMessage,
                showOtpLoader,
            }, onCreateUserSuccess: onCreateUserSuccess, onEditUserSuccess: onEditUserSuccess })));
};
export default UsersContainer;
//# sourceMappingURL=users.container.js.map